import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

const BASE = '/email/automations';

export interface AutomationRuleApi {
  _id: string;
  name: string;
  vendor: string;
  isActive: boolean;
  nodes: any[];
  connections: { from: string; to: string }[];
  schedule: string;
  scheduleTime?: string;
  totalSent: number;
  lastTriggeredAt?: string;
  lastError?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExecutionLogApi {
  _id: string;
  automationId: string;
  leadEmail: string;
  status: 'sent' | 'failed' | 'skipped' | 'filtered';
  error?: string;
  triggerType?: string;
  messageId?: string;
  trackingId?: string;
  subject?: string;
  createdAt?: string;
}

export const useAutomationApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();

  const list = async (): Promise<AutomationRuleApi[]> => {
    try {
      const response: any = await sendAuthGuardedRequest(ApiMethod.GET, BASE);
      return response || [];
    } catch { return []; }
  };

  const getById = async (id: string): Promise<AutomationRuleApi | null> => {
    try {
      return await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/${id}`) as any;
    } catch { return null; }
  };

  const create = async (data: Partial<AutomationRuleApi>): Promise<AutomationRuleApi | null> => {
    try {
      const result = await sendAuthGuardedRequest(ApiMethod.POST, BASE, data) as any;
      console.log('[Automation] Created:', result?._id);
      return result;
    } catch (e) { console.error('[Automation] Create failed:', e); return null; }
  };

  const update = async (id: string, data: Partial<AutomationRuleApi>): Promise<AutomationRuleApi | null> => {
    try {
      const result = await sendAuthGuardedRequest(ApiMethod.PATCH, `${BASE}/${id}`, data) as any;
      return result;
    } catch (e) { console.error('[Automation] Update failed:', e); return null; }
  };

  const remove = async (id: string): Promise<boolean> => {
    try {
      await sendAuthGuardedRequest(ApiMethod.DELETE, `${BASE}/${id}`);
      return true;
    } catch { return false; }
  };

  const duplicate = async (id: string): Promise<AutomationRuleApi | null> => {
    try {
      return await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/${id}/duplicate`) as any;
    } catch { return null; }
  };

  const toggle = async (id: string, isActive: boolean): Promise<AutomationRuleApi | null> => {
    try {
      return await sendAuthGuardedRequest(ApiMethod.PATCH, `${BASE}/${id}/toggle`, { isActive }) as any;
    } catch { return null; }
  };

  const getHistory = async (id: string, limit = 50): Promise<ExecutionLogApi[]> => {
    try {
      const response: any = await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/${id}/history?limit=${limit}`);
      return response || [];
    } catch { return []; }
  };

  const testRun = async (id: string, testEmail: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const result: any = await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/${id}/test`, { testEmail });
      return { success: result?.success, message: result?.error || result?.message };
    } catch { return { success: false, message: 'Test failed' }; }
  };

  return { list, getById, create, update, remove, duplicate, toggle, getHistory, testRun };
};
